import { useCallback, useEffect, useState } from 'react';
import {
  useNavigate,
  useLocation,
  useParams,
  generatePath,
  useMatch,
} from 'react-router-dom';
import {
  BlacklistDataContent,
  ButtonPseudo,
  InternalSidepanel,
  SidePanel,
  SidePanelType,
  TransactionStatusChange,
  PermissionEnum,
  responseErrorsResolver,
  ResponseError,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { EnumTabs } from '@ui/src/components/transactions-sidepanels/types';
import StatusesUrl from '../../../types/statuses-url';
import {
  changePanelState,
  clearTransactionFeeState,
  setBlacklistData,
  setBlacklistDataVisible,
} from '../store/slices/transaction-fee';
import ROUTES from '../../../routes';
import {
  useLazyGetInternalTransactionByIdQuery,
  useLazyGetInternalTransactionWarningsQuery,
} from '../../internal/store/services';
import {
  useLazyGetTransactionLogsQuery,
  useLazyGetStatusesTransactionByIdQuery,
} from '../../../store/statuses/services';
import { clearStatusesState } from '../../../store/statuses/slice';
import {
  useLazyGetTransactionsDictionariesQuery,
  useLazyGetTransactionsDictionariesWithoutGLQuery,
  useLazyGetTransactionsBaseCurrencyQuery,
  useLazyGetTransactionsCurrenciesQuery,
  useLazyGetTransactionsCountriesQuery,
} from '../../../store/dictionaries/services';
import useRetry from '../../outgoing/helpers/use-retry';
import { useDeleteTransactionMutation } from '../../transactions/store/services';
import { useLazyGetGeneralLedgerListQuery } from '../store/services';

const TransactionFee = (): React.JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { transactionId } = useParams();
  const { search } = useLocation();
  const match = useMatch(ROUTES.GENERAL_LEDGER_FEE);
  const [isChangedTab, setIsChangedTab] = useState<boolean>(false);
  const [deleteTransactionError, setDeleteTransactionError] = useState('');

  const {
    transactionFee: {
      transaction,
      panelIsOpen,
      warnings,
      blacklistData,
      blacklistDataVisible,
    },
    generalLedger: {
      list: { selectedTake },
    },
    statuses: { statuses },
    transactionsDictionaries: {
      activePermissions,
      pspPartnerAccounts,
      countries,
      currencies,
      baseCurrency,
      accounts,
    },
  } = useAppSelector((state) => state);

  const [getDictionaries] = useLazyGetTransactionsDictionariesQuery();
  const [getDictionariesWithoutGL] =
    useLazyGetTransactionsDictionariesWithoutGLQuery();
  const [getTransactionBaseCurrency] =
    useLazyGetTransactionsBaseCurrencyQuery();
  const [getTransactionCurrencies] = useLazyGetTransactionsCurrenciesQuery();
  const [getTransactionCountries] = useLazyGetTransactionsCountriesQuery();

  const [getTransactionFeeById, { isLoading }] =
    useLazyGetInternalTransactionByIdQuery();
  const [getTransactionFeeWarnings, { isLoading: isWarningLoading }] =
    useLazyGetInternalTransactionWarningsQuery();
  const [getTransactionsList] = useLazyGetGeneralLedgerListQuery();

  const [
    deleteTransaction,
    {
      isLoading: deleteTransactionInProgress,
      isSuccess: deleteTransactionIsSuccess,
    },
  ] = useDeleteTransactionMutation();

  const [
    getStatusesTransactionById,
    { isLoading: isLoadingStatuses, isFetching: isFetchingStatuses },
  ] = useLazyGetStatusesTransactionByIdQuery();

  const onDeleteTransaction = async (id: string) => {
    const response = (await deleteTransaction(id)) as { error?: { data: any } };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error as ResponseError,
        setError: () => {},
        setGlobalError: setDeleteTransactionError,
      });
    }
  };

  const handleClose = () => {
    dispatch(clearTransactionFeeState());

    setTimeout(() => {
      navigate(`${ROUTES.GENERAL_LEDGER}${search || ''}`);
    }, 100);
  };

  const { getTransaction, onClearTimer } = useRetry({
    getTransactionFunction: getTransactionFeeById,
    id: transactionId as string,
    isRetry: true,
    getStatusesFunction: getStatusesTransactionById,
    getTransactionWarningsFunction: getTransactionFeeWarnings,
    statusPermission: activePermissions?.includes(
      PermissionEnum.TRANSACTIONS_INTERNAL_EDIT_ACTION,
    ),
    statusUrl: StatusesUrl.INTERNAL,
  });

  const [getLogsTransaction, { isLoading: isLoadingLog }] =
    useLazyGetTransactionLogsQuery();

  const getLogsTransactionAction = useCallback(() => {
    getLogsTransaction({ id: transactionId as string, url: '/internal' });
  }, [transactionId, transaction]);

  const handleCloseBlacklistData = () => {
    dispatch(setBlacklistDataVisible(false));
    dispatch(setBlacklistData(undefined));
    dispatch(changePanelState({ state: true, transactionId }));
  };

  useEffect(() => {
    if (deleteTransactionIsSuccess) {
      handleClose();

      getTransactionsList(
        `?take=${selectedTake?.toString() || '25'}${search.replace('?', '&')}`,
      );
    }
  }, [deleteTransactionIsSuccess, selectedTake, search]);

  useEffect(() => {
    if (!accounts) {
      getDictionaries();
    }

    if (!pspPartnerAccounts) {
      getDictionariesWithoutGL();
    }

    if (!currencies) {
      getTransactionCurrencies();
    }

    if (!countries) {
      getTransactionCountries();
    }

    if (!baseCurrency) {
      getTransactionBaseCurrency();
    }
  }, [baseCurrency, currencies, countries, pspPartnerAccounts, accounts]);

  useEffect(() => {
    dispatch(changePanelState({ state: true, transactionId }));

    return () => {
      dispatch(clearTransactionFeeState());
      dispatch(clearStatusesState());
      onClearTimer(true);
    };
  }, [dispatch]);

  useEffect(() => {
    if (panelIsOpen && transactionId) {
      getTransaction();
    }
  }, [panelIsOpen, transactionId]);

  useEffect(() => {
    if (transaction) {
      getLogsTransactionAction();
    }
  }, [transaction]);

  useEffect(() => {
    if (match && transactionId) {
      navigate(
        `${generatePath(ROUTES.GENERAL_LEDGER_FEE_GENERAL, {
          transactionId,
        })}${search || ''}`,
      );
    }
  }, [match, transactionId, warnings]);

  useEffect(() => {
    if (!isChangedTab && transactionId) {
      setIsChangedTab(true);

      if (warnings && warnings.length) {
        navigate(
          `${generatePath(ROUTES.GENERAL_LEDGER_FEE_WARNING, {
            transactionId,
          })}${search || ''}`,
        );
      }
    }
  }, [warnings, isChangedTab]);

  const isLoadingPanel =
    isLoading ||
    isLoadingLog ||
    isLoadingStatuses ||
    (isWarningLoading && !transaction) ||
    Boolean(!transaction && transactionId);

  return (
    <>
      <InternalSidepanel
        sidepanelExpandedKey="fee-transaction"
        panelIsOpen={panelIsOpen}
        handleClose={handleClose}
        title="Fee transaction"
        isLoading={isLoadingPanel}
        transactionId={transaction?.id}
        deleteTransactionError={deleteTransactionError}
        deleteTransactionInProgress={deleteTransactionInProgress}
        transactionFee
        tabs={{
          generalPath: `${generatePath(ROUTES.GENERAL_LEDGER_FEE_GENERAL, {
            transactionId: transactionId as string,
          })}${search || ''}`,
          amlPath: '',
          logPath: `${generatePath(ROUTES.GENERAL_LEDGER_FEE_LOG, {
            transactionId: transactionId as string,
          })}${search || ''}`,
          changeLogPath: `${generatePath(ROUTES.GENERAL_LEDGER_FEE_CHANGE_LOG, {
            transactionId: transactionId as string,
          })}${search || ''}`,
          warningPath: `${generatePath(ROUTES.GENERAL_LEDGER_FEE_WARNING, {
            transactionId: transactionId as string,
          })}${search || ''}`,
        }}
        isWarning={
          transaction && !isWarningLoading && Boolean(warnings?.length)
        }
        onDeleteTransactions={onDeleteTransaction}
      >
        {statuses && !isLoading && (
          <TransactionStatusChange
            transaction={statuses}
            isLoadingStatuses={isFetchingStatuses}
            baseellaStatus={transaction?.baseellaStatus}
            activeTab={EnumTabs.GENERAL}
          />
        )}
      </InternalSidepanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistDataVisible}
        onClose={handleCloseBlacklistData}
        title={blacklistData?.fullName as string}
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="fee-blacklist"
      >
        <BlacklistDataContent data={blacklistData} />
      </SidePanel>
      <div id="blacklist-data-sidepanel" />
    </>
  );
};

export default TransactionFee;
